import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import FormInput from '../../components/ui/FormInput';
import SeletorBancos from './SeletorBancos';
import BankCard from './BankCard';
import GraficoComparativo from './GraficoComparativo';
import ResumoCards from './ResumoCards';
import TabelaParcelas from './TabelaParcelas';
import { formatarMoeda } from '../../utils/formatters';
import { simularMultibanco } from '../../services/simuladorService';
import EvolucaoComparativa from './EvolucaoComparativa';
import { exportarPDFBanco, exportarPDFEvolucao } from '../../services/pdfExport';
import { useAuth } from '../../contexts/AuthContext';
import {  updateClientAfterSimulation } from '../../services/clientService';
import { createSimulation, subscribeSimulationsByRole } from '../../services/simulacaoService';
import { savePdf, subscribePdfsByRole } from '../../services/PdfService';
import { subscribeClientsByRole } from '../../services/clientService';
import { useUserProfile } from '../../contexts/UserContext';
import { useTheme } from '../../contexts/Themecontext';
import { useSubscriptionGuard } from '../../services/useSubscriptionGuard';

// ======== MÁSCARAS ========
function maskMoeda(value) {
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10);
  return (num / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseMoeda(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const limpo = String(value).replace(/[^\d,]/g, '').replace(',', '.');
  return parseFloat(limpo) || 0;
}

function maskCPF(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function maskTelefone(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

// Converte número puro em string mascarada (para preencher do cliente)
function numToMoeda(num) {
  if (!num || num <= 0) return '';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function SimuladorPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [paginaAtual, setPaginaAtual] = useState('simulador');
  const comparativoRef = useRef(null);

  // Clientes do Firestore
  const [clients, setClients] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  // Campos do formulário (strings com máscara)
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [renda, setRenda] = useState('');
  const [valorImovel, setValorImovel] = useState('');
  const [valorFinanciado, setValorFinanciado] = useState('');
  const [prazoMeses, setPrazoMeses] = useState(null);
  const [dataNascimento, setDataNascimento] = useState('');
  const [taxasSelecionadas, setTaxasSelecionadas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [bancoSelecionado, setBancoSelecionado] = useState(null);
  const [mostrarTabela, setMostrarTabela] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
const { profile } = useUserProfile();
const { account } = useTheme();
const { canPerform } = useSubscriptionGuard();
  // Carregar clientes
  useEffect(() => {
  if (!profile) return;
  const unsub = subscribeClientsByRole(profile, setClients);
  const unsub2 = subscribeSimulationsByRole(profile, setSimulations);
  const unsub3 = subscribePdfsByRole(profile, setPdfs);
  return () => { unsub(); unsub2(); unsub3(); };
}, [profile]);

  // Preencher campos quando clientes carregam e tem clienteId via navigation state
  useEffect(() => {
    const idFromNav = location.state?.clienteId;
    if (idFromNav && clients.length > 0) {
      setClienteId(idFromNav);
      preencherCliente(idFromNav);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, clients]);

  const preencherCliente = (id) => {
    const c = clients.find((cl) => cl.id === id);
    if (!c) return;
    if (c.nome) setNomeCompleto(c.nome);
    if (c.cpf) setCpf(c.cpf);
    if (c.telefone) setTelefone(c.telefone);
    if (c.email) setEmail(c.email);
    if (c.valorImovel) {
      const num = parseMoeda(c.valorImovel);
      setValorImovel(num > 0 ? numToMoeda(num) : 'R$ 500.000,00');
    }
    if (c.valorFinanciado) {
      const num = parseMoeda(c.valorFinanciado);
      setValorFinanciado(num > 0 ? numToMoeda(num) : 'R$ 400.000,00');
    }
    if (c.prazo) setPrazoMeses(Number(c.prazo) || 120);
    if (c.dataNascimento) setDataNascimento(c.dataNascimento);
  };

  const handleClienteSelect = (e) => {
    const id = e.target.value;
    setClienteId(id);
    setSalvo(false);
    if (id) preencherCliente(id);
  };

  const handleSimular = async () => {
    if (Object.keys(taxasSelecionadas).length === 0) {
      setError('Selecione ao menos um banco para simular.');
      return;
    }
    setLoading(true);
    setError(null);
    setSalvo(false);
    try {
      // Extrair valores numéricos das strings mascaradas
      const viNum = parseMoeda(valorImovel);
      const vfNum = parseMoeda(valorFinanciado);
      const rNum = parseMoeda(renda);

      const data = await simularMultibanco({
        valorImovel: viNum,
        valorFinanciado: vfNum,
        prazoMeses,
        dataNascimento,
        taxasPorBanco: taxasSelecionadas,
        nomeCompleto, cpf, telefone, email,
        renda: rNum,
      });
      setResultado(data);
      setBancoSelecionado(data.simulacoes[0]?.banco || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarSimulacao = async () => {
    if (!resultado || !user) return;

    // Verificar limite de simulações
    const simCheck = canPerform('simulacoes', simulations?.length || 0);
    if (!simCheck.allowed) {
      setError(simCheck.reason);
      return;
    }

    setSalvando(true);
    try {
      const viNum = parseMoeda(valorImovel);
      const vfNum = parseMoeda(valorFinanciado);
      const menorTotal = Math.min(...resultado.simulacoes.map((s) => s.totais.totalAPagar));
      const melhorSim = resultado.simulacoes.find((s) => s.totais.totalAPagar === menorTotal);

      const simData = {
        bancos: resultado.simulacoes.map((s) => ({
          nome: s.nomeBanco || s.banco,
          cor: s.cor || '#666',
          parcela: formatarMoeda(s.parcelas[0]?.prestacao || 0),
          totalAPagar: s.totais.totalAPagar,
          totalJuros: s.totais.totalJuros,
        })),
        melhorBanco: melhorSim
          ? { nome: melhorSim.nomeBanco || melhorSim.banco, cor: melhorSim.cor || 'var(--accent)', parcela: formatarMoeda(melhorSim.parcelas[0]?.prestacao || 0) }
          : null,
        valorImovel: formatarMoeda(viNum),
        valorFinanciado: formatarMoeda(vfNum),
        prazo: prazoMeses,
        dataNascimento,
      };

      await createSimulation(user.uid, clienteId || null, simData, profile);
      if (clienteId) {
        await updateClientAfterSimulation(clienteId, simData);
      }
      setSalvo(true);
    } catch (err) {
      console.error('Erro ao salvar simulação:', err);
      setError('Erro ao salvar simulação.');
    }
    setSalvando(false);
  };

  const simulacaoAtual = resultado?.simulacoes?.find((s) => s.banco === bancoSelecionado);
  const dadosComparativo = resultado?.simulacoes?.map((s) => ({
    banco: s.nomeBanco, totalJuros: s.totais.totalJuros, totalAPagar: s.totais.totalAPagar,
  })) || [];
  const menorTotal = resultado?.simulacoes ? Math.min(...resultado.simulacoes.map((s) => s.totais.totalAPagar)) : 0;
  const economia = resultado?.simulacoes?.length >= 2
    ? Math.max(...resultado.simulacoes.map((s) => s.totais.totalAPagar)) - menorTotal : null;
  const bancosCount = Object.keys(taxasSelecionadas).length;

  if (paginaAtual === 'evolucao' && resultado) {
    const vfNum = parseMoeda(valorFinanciado);
    const viNum = parseMoeda(valorImovel);
    return (
      <EvolucaoComparativa
        resultado={resultado}
        valorFinanciado={vfNum}
        prazoMeses={prazoMeses}
        onVoltar={() => setPaginaAtual('simulador')}
        onExportarPDF={async () => {
          // Verificar limite de PDFs
          const pdfCheck = canPerform('pdfs', pdfs.length);
          if (!pdfCheck.allowed) { alert(pdfCheck.reason); return; }
          const result = await exportarPDFEvolucao({ resultado, valorFinanciado: vfNum, valorImovel: viNum, prazoMeses, account });
          if (result?.blob && user) {
            await savePdf(user.uid, result.blob, {
              clientId: clienteId || null,
              clientNome: nomeCompleto || 'Simulação avulsa',
              banco: 'Comparativo',
              bancoCor: '#8B5CF6',
              valorFinanciado: formatarMoeda(vfNum),
              prazo: prazoMeses,
              tipo: 'evolucao',
            }, profile);
          }
        }}
      />
    );
  }

  const selectStyle = {
    width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)',
    borderRadius: 'var(--radius-xs)', fontFamily: 'var(--font)', fontSize: '0.82rem',
    color: 'var(--text)', background: 'var(--white)', outline: 'none', cursor: 'pointer',
  };

  return (
    <div className="sim-layout" style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20 }}>
      {/* ── FORMULÁRIO ── */}
      <Card className="sim-form" style={{ position: 'sticky', top: 88, alignSelf: 'start' }}>
        <div style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚡ Simulação Rápida
        </div>

        {/* Seletor de cliente */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>
            Vincular a cliente (opcional)
          </label>
          <select value={clienteId} onChange={handleClienteSelect} style={selectStyle}>
            <option value="">Simulação avulsa</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
          {clienteId && (
            <p style={{ fontSize: '0.68rem', color: 'var(--accent)', marginTop: 4 }}>
              ✓ Simulação será vinculada ao cliente no CRM
            </p>
          )}
        </div>

        <div style={{ borderTop: '1px solid var(--border)', margin: '0 0 16px', paddingTop: 16 }} />

        <FormInput label="Nome Completo" value={nomeCompleto} onChange={(e) => setNomeCompleto(e.target.value)} placeholder="João Silva" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormInput label="CPF" value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))} placeholder="000.000.000-00" maxLength={14} />
          <FormInput label="Telefone" value={telefone} onChange={(e) => setTelefone(maskTelefone(e.target.value))} placeholder="(61) 99999-0000" maxLength={15} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormInput label="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.com" />
          <FormInput label="Renda" value={renda} onChange={(e) => setRenda(maskMoeda(e.target.value))} placeholder="R$ 20.000,00" />
        </div>

        <div style={{ borderTop: '1px solid var(--border)', margin: '8px 0 16px', paddingTop: 16 }} />

        <FormInput label="Valor do Imóvel" value={valorImovel} onChange={(e) => setValorImovel(maskMoeda(e.target.value))} placeholder="R$ 500.000,00" />
        <FormInput label="Valor Financiado" value={valorFinanciado} onChange={(e) => setValorFinanciado(maskMoeda(e.target.value))} placeholder="R$ 400.000,00" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormInput label="Prazo (meses)" type="number" value={prazoMeses} onChange={(e) => setPrazoMeses(Number(e.target.value))} placeholder="120" maxLength={3} />
          <FormInput label="Nascimento" type="date" value={dataNascimento} onChange={(e) => setDataNascimento(e.target.value)} />
        </div>

        <div style={{ borderTop: '1px solid var(--border)', margin: '8px 0 16px', paddingTop: 16 }} />

        <SeletorBancos taxasSelecionadas={taxasSelecionadas} setTaxasSelecionadas={setTaxasSelecionadas} />

        <button
          onClick={handleSimular}
          disabled={loading}
          style={{
            width: '100%', padding: 12, background: 'var(--accent)', color: 'var(--primary)',
            border: 'none', borderRadius: 'var(--radius-xs)', fontFamily: 'var(--font)',
            fontSize: '0.9rem', fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
            marginTop: 16, opacity: loading ? 0.7 : 1, transition: 'all 0.2s',
          }}
        >
          {loading ? 'Simulando...' : `Simular ${bancosCount || ''} banco${bancosCount !== 1 ? 's' : ''}`}
        </button>
      </Card>

      {/* ── RESULTADOS ── */}
      <div>
        {error && (
          <Card style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: 20 }}>
            <p style={{ color: 'var(--danger)', fontSize: '0.85rem', margin: 0 }}>⚠️ {error}</p>
          </Card>
        )}

        {!resultado && !loading && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: 400, color: 'var(--text-muted)', textAlign: 'center',
          }}>
            <span style={{ fontSize: '3rem', marginBottom: 16 }}>🏦</span>
            <p style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 4 }}>Selecione os bancos e preencha os dados</p>
            <p style={{ fontSize: '0.85rem' }}>Os resultados do comparativo aparecerão aqui</p>
          </div>
        )}

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
            <div style={{
              width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--accent)',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <p style={{ marginTop: 16, color: 'var(--text-muted)', fontSize: '0.88rem' }}>Simulando financiamentos...</p>
          </div>
        )}

        {resultado && (
          <>
            {economia && (
              <Card style={{ background: 'rgba(0,214,143,0.04)', border: '1px solid rgba(0,214,143,0.15)', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.3rem' }}>💡</span>
                  <div>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Economia possível entre bancos: </span>
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: '#059669' }}>{formatarMoeda(economia)}</span>
                  </div>
                </div>
              </Card>
            )}

            <div ref={comparativoRef}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {resultado.simulacoes.map((sim) => (
                  <BankCard
                    key={sim.banco}
                    sim={sim}
                    isBest={sim.totais.totalAPagar === menorTotal}
                    isSelected={sim.banco === bancoSelecionado}
                    onClick={() => setBancoSelecionado(sim.banco)}
                  />
                ))}
              </div>
              <GraficoComparativo dados={dadosComparativo} />
            </div>

            {/* Ações */}
            <div className="sim-actions" style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
              <Button
                variant="danger"
                onClick={async () => {
                  // Verificar limite de PDFs
                  const pdfCheck2 = canPerform('pdfs', pdfs.length);
                  if (!pdfCheck2.allowed) { alert(pdfCheck2.reason); return; }
                  const vfNum = parseMoeda(valorFinanciado);
                  const result = await exportarPDFBanco({ simulacaoAtual, valorFinanciado: vfNum, prazoMeses, comparativoRef, account });
                  if (result?.blob && user) {
                    await savePdf(user.uid, result.blob, {
                      clientId: clienteId || null,
                      clientNome: nomeCompleto || 'Simulação avulsa',
                      banco: simulacaoAtual?.nomeBanco || simulacaoAtual?.banco,
                      bancoCor: simulacaoAtual?.cor,
                      valorFinanciado: formatarMoeda(vfNum),
                      prazo: prazoMeses,
                      tipo: 'banco',
                    });
                  }
                }}
              >
                📄 Exportar PDF
              </Button>
              <Button variant="primary" onClick={() => setPaginaAtual('evolucao')}>
                📊 Ver Evolução
              </Button>
              <Button
                variant="accent"
                onClick={handleSalvarSimulacao}
                disabled={salvando || salvo}
              >
                {salvo ? '✅ Simulação Salva' : salvando ? 'Salvando...' : '💾 Salvar Simulação'}
              </Button>
            </div>

            {salvo && (
              <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--accent)', marginTop: 8 }}>
                Simulação salva com sucesso{clienteId ? ' e vinculada ao cliente no CRM' : ''}.
              </p>
            )}

            {simulacaoAtual && (
              <>
                <div style={{ marginTop: 24, marginBottom: 8 }}>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'var(--accent)',
                  }}>
                    Detalhes — {simulacaoAtual.nomeBanco || simulacaoAtual.banco}
                  </span>
                </div>
                <ResumoCards simulacao={simulacaoAtual} />
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                  <Button variant="ghost" onClick={() => setMostrarTabela(!mostrarTabela)}>
                    {mostrarTabela ? '📊 Ocultar Tabela' : '📋 Ver Tabela Detalhada'}
                  </Button>
                </div>
                {mostrarTabela && <TabelaParcelas parcelas={simulacaoAtual.parcelas} />}
              </>
            )}
          </>
        )}
      </div>
      <style>{`
        @media(max-width:768px){
          .sim-layout{grid-template-columns:1fr!important;gap:16px!important}
          .sim-form{position:relative!important;top:0!important}
          .sim-actions{flex-direction:column!important}
          .sim-actions button,.sim-actions a{width:100%}
        }
      `}</style>
    </div>
  );
}